import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Plus, Save, ArrowLeft, GripVertical, Trash2, Settings, Copy, Check, Code, Share, Smartphone, Tablet, Monitor, Palette, ListTree, ZoomIn, ZoomOut, BookmarkPlus, Loader2, Upload, Link as LinkIcon, X, GitBranch } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { apiRequest } from '../config/api';
import { buttonStyles } from '../utils/colors';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import IntegrationManager from '../components/IntegrationManager';
import { useKeyboardShortcut, getModKey } from '../hooks/useKeyboardShortcuts';
import FormThemePanel from '../components/FormThemePanel';
import StepConditionEditor from '../components/StepConditionEditor';
import DisqualificationEditor from '../components/DisqualificationEditor';

// VersionDiff component for comparing form versions
function VersionDiff({ selectedVersion, currentVersion, versions }) {
  if (!selectedVersion || !currentVersion) {
    return <div className="text-gray-500 dark:text-gray-400">No version selected for comparison</div>;
  }

  // Get the previous version for comparison
  const getPreviousVersion = (version) => {
    const sortedVersions = [...versions].sort((a, b) => a.version_number - b.version_number);
    const currentIndex = sortedVersions.findIndex(v => v.id === version.id);
    return currentIndex > 0 ? sortedVersions[currentIndex - 1] : null;
  };

  const previousVersion = getPreviousVersion(selectedVersion);
  const compareWith = previousVersion || currentVersion;

  // Helper function to extract fields from version
  const extractFields = (version) => {
    if (!version?.fields?.steps) return [];
    return version.fields.steps.flatMap((step, stepIndex) => 
      (step.fields || []).map(field => ({
        ...field,
        stepTitle: step.title,
        stepIndex: stepIndex,
        stepId: step.id
      }))
    );
  };

  // Helper function to extract steps from version
  const extractSteps = (version) => {
    if (!version?.fields?.steps) return [];
    return version.fields.steps.map((step, index) => ({
      ...step,
      index: index
    }));
  };

  // Helper function to compare steps
  const compareSteps = (selectedSteps, compareSteps) => {
    const selectedMap = new Map(selectedSteps.map(s => [s.id, s]));
    const compareMap = new Map(compareSteps.map(s => [s.id, s]));
    
    const differences = [];
    
    // Find added steps
    selectedSteps.forEach(step => {
      if (!compareMap.has(step.id)) {
        differences.push({
          type: 'added',
          step: step
        });
      }
    });
    
    // Find removed steps
    compareSteps.forEach(step => {
      if (!selectedMap.has(step.id)) {
        differences.push({
          type: 'removed',
          step: step
        });
      }
    });
    
    // Find modified steps
    selectedSteps.forEach(step => {
      const compareStep = compareMap.get(step.id);
      if (compareStep) {
        const changes = [];
        
        // Compare all step properties
        const stepKeys = new Set([...Object.keys(step), ...Object.keys(compareStep)]);
        stepKeys.forEach(key => {
          // Skip fields array and index as they're handled separately
          if (key === 'fields' || key === 'index') return;
          
          if (JSON.stringify(step[key]) !== JSON.stringify(compareStep[key])) {
            changes.push({ 
              property: key, 
              old: compareStep[key], 
              new: step[key] 
            });
          }
        });
        
        // Check for position changes
        if (step.index !== compareStep.index) {
          changes.push({ 
            property: 'position', 
            old: compareStep.index + 1, 
            new: step.index + 1 
          });
        }
        
        if (changes.length > 0) {
          differences.push({
            type: 'modified',
            step: step,
            changes: changes
          });
        }
      }
    });
    
    return differences;
  };
  const compareSettings = (version1, version2) => {
    const settings1 = version1?.settings || {};
    const settings2 = version2?.settings || {};
    
    const allKeys = new Set([...Object.keys(settings1), ...Object.keys(settings2)]);
    const differences = [];
    
    allKeys.forEach(key => {
      const val1 = settings1[key];
      const val2 = settings2[key];
      
      if (JSON.stringify(val1) !== JSON.stringify(val2)) {
        differences.push({
          key,
          old: val2,
          new: val1,
          type: val1 === undefined ? 'removed' : val2 === undefined ? 'added' : 'modified'
        });
      }
    });
    
    return differences;
  };

  // Helper function to compare fields
  const compareFields = (selectedFields, compareFields) => {
    const selectedMap = new Map(selectedFields.map(f => [f.id, f]));
    const compareMap = new Map(compareFields.map(f => [f.id, f]));
    
    const differences = [];
    
    // Find added fields
    selectedFields.forEach(field => {
      if (!compareMap.has(field.id)) {
        differences.push({
          type: 'added',
          field: field
        });
      }
    });
    
    // Find removed fields
    compareFields.forEach(field => {
      if (!selectedMap.has(field.id)) {
        differences.push({
          type: 'removed',
          field: field
        });
      }
    });
    
    // Find modified fields - compare ALL properties
    selectedFields.forEach(field => {
      const compareField = compareMap.get(field.id);
      if (compareField) {
        const changes = [];
        
        // Get all unique keys from both field objects
        const allKeys = new Set([...Object.keys(field), ...Object.keys(compareField)]);
        
        allKeys.forEach(key => {
          // Skip internal properties added during extraction
          if (key === 'stepTitle' || key === 'stepIndex' || key === 'stepId') return;
          
          const newValue = field[key];
          const oldValue = compareField[key];
          
          // Deep comparison for all properties
          if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
            changes.push({ 
              property: key, 
              old: oldValue, 
              new: newValue 
            });
          }
        });
        
        if (changes.length > 0) {
          differences.push({
            type: 'modified',
            field: field,
            changes: changes
          });
        }
      }
    });
    
    return differences;
  };

  const selectedFields = extractFields(selectedVersion);
  const compareWithFields = extractFields(compareWith);
  const selectedSteps = extractSteps(selectedVersion);
  const compareWithSteps = extractSteps(compareWith);
  
  const fieldDifferences = compareFields(selectedFields, compareWithFields);
  const stepDifferences = compareSteps(selectedSteps, compareWithSteps);
  const settingsDifferences = compareSettings(selectedVersion, compareWith);

  const hasChanges = fieldDifferences.length > 0 || stepDifferences.length > 0 || settingsDifferences.length > 0 || 
                    selectedVersion.title !== compareWith.title || 
                    selectedVersion.description !== compareWith.description;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
          <span>Comparing</span>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-red-500 rounded-full"></div>
            <span className="font-medium">v{compareWith.version_number}</span>
            <span className="text-gray-500">({new Date(compareWith.created_at).toLocaleDateString()})</span>
          </div>
          <span className="text-gray-400">→</span>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span className="font-medium">v{selectedVersion.version_number}</span>
            <span className="text-gray-500">({new Date(selectedVersion.created_at).toLocaleDateString()})</span>
          </div>
        </div>
      </div>

      {!hasChanges ? (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-gray-500 dark:text-gray-400">No changes detected between these versions</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Form Details Changes */}
          {(selectedVersion.title !== compareWith.title || selectedVersion.description !== compareWith.description) && (
            <div>
              <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">Form Details</h4>
              <div className="space-y-3">
                {selectedVersion.title !== compareWith.title && (
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                    <div className="font-medium text-sm text-gray-700 dark:text-gray-300 mb-2">Title</div>
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2 text-sm">
                        <span className="text-red-600">−</span>
                        <span className="text-gray-600 dark:text-gray-400 line-through">{compareWith.title}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm">
                        <span className="text-green-600">+</span>
                        <span className="text-gray-900 dark:text-gray-100">{selectedVersion.title}</span>
                      </div>
                    </div>
                  </div>
                )}
                {selectedVersion.description !== compareWith.description && (
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                    <div className="font-medium text-sm text-gray-700 dark:text-gray-300 mb-2">Description</div>
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2 text-sm">
                        <span className="text-red-600">−</span>
                        <span className="text-gray-600 dark:text-gray-400 line-through">{compareWith.description || '(empty)'}</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm">
                        <span className="text-green-600">+</span>
                        <span className="text-gray-900 dark:text-gray-100">{selectedVersion.description || '(empty)'}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Field Changes */}
          {fieldDifferences.length > 0 && (
            <div>
              <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">Field Changes</h4>
              <div className="space-y-3">
                {fieldDifferences.map((diff, index) => (
                  <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className={`w-3 h-3 rounded-full ${
                        diff.type === 'added' ? 'bg-green-500' : 
                        diff.type === 'removed' ? 'bg-red-500' : 'bg-brand-500'
                      }`}></div>
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {diff.field.label}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        diff.type === 'added' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                        diff.type === 'removed' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                        'bg-brand-100 text-brand-800 dark:bg-brand-900 dark:text-brand-200'
                      }`}>
                        {diff.type}
                      </span>
                      {diff.field.stepTitle && (
                        <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 rounded-full">
                          {diff.field.stepTitle}
                        </span>
                      )}
                    </div>
                    
                    {diff.type === 'modified' && diff.changes && (
                      <div className="space-y-2">
                        {diff.changes.map((change, changeIndex) => (
                          <div key={changeIndex} className="text-sm">
                            <div className="font-medium text-gray-700 dark:text-gray-300 mb-1">
                              {change.property}:
                            </div>
                            <div className="space-y-1 ml-4">
                              <div className="flex items-center space-x-2">
                                <span className="text-red-600">−</span>
                                <span className="text-gray-600 dark:text-gray-400">
                                  {Array.isArray(change.old) ? change.old.join(', ') : String(change.old || '(empty)')}
                                </span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="text-green-600">+</span>
                                <span className="text-gray-900 dark:text-gray-100">
                                  {Array.isArray(change.new) ? change.new.join(', ') : String(change.new || '(empty)')}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {diff.type !== 'modified' && (
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        Type: {diff.field.type} {diff.field.required && '(required)'}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step Changes */}
          {stepDifferences.length > 0 && (
            <div>
              <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">Step Changes</h4>
              <div className="space-y-3">
                {stepDifferences.map((diff, index) => (
                  <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className={`w-3 h-3 rounded-full ${
                        diff.type === 'added' ? 'bg-green-500' : 
                        diff.type === 'removed' ? 'bg-red-500' : 'bg-brand-500'
                      }`}></div>
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {diff.step.title || `Step ${diff.step.index + 1}`}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        diff.type === 'added' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                        diff.type === 'removed' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                        'bg-brand-100 text-brand-800 dark:bg-brand-900 dark:text-brand-200'
                      }`}>
                        {diff.type}
                      </span>
                    </div>
                    
                    {diff.type === 'modified' && diff.changes && (
                      <div className="space-y-2">
                        {diff.changes.map((change, changeIndex) => (
                          <div key={changeIndex} className="text-sm">
                            <div className="font-medium text-gray-700 dark:text-gray-300 mb-1">
                              {change.property}:
                            </div>
                            <div className="space-y-1 ml-4">
                              <div className="flex items-center space-x-2">
                                <span className="text-red-600">−</span>
                                <span className="text-gray-600 dark:text-gray-400">
                                  {String(change.old || '(empty)')}
                                </span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="text-green-600">+</span>
                                <span className="text-gray-900 dark:text-gray-100">
                                  {String(change.new || '(empty)')}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {diff.type !== 'modified' && (
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {diff.step.description || 'No description'}
                        {diff.step.fields && (
                          <span className="ml-2">({diff.step.fields.length} fields)</span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Settings Changes */}
          {settingsDifferences.length > 0 && (
            <div>
              <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-3">Settings Changes</h4>
              <div className="space-y-3">
                {settingsDifferences.map((diff, index) => (
                  <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {diff.key}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        diff.type === 'added' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                        diff.type === 'removed' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                        'bg-brand-100 text-brand-800 dark:bg-brand-900 dark:text-brand-200'
                      }`}>
                        {diff.type}
                      </span>
                    </div>
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center space-x-2">
                        <span className="text-red-600">−</span>
                        <span className="text-gray-600 dark:text-gray-400">
                          {JSON.stringify(diff.old) || '(empty)'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-green-600">+</span>
                        <span className="text-gray-900 dark:text-gray-100">
                          {JSON.stringify(diff.new) || '(empty)'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Utility function to generate slug from label
const generateSlug = (text) => {
  if (!text) return '';
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/[\s-]+/g, '-') // Replace spaces and multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

const getFieldSlugSource = (field) => {
  if (field.type === 'heading' || field.type === 'paragraph') return field.content;
  if (field.type === 'image') return field.altText;
  return field.label;
};

const generateFieldSlug = (field) => generateSlug(getFieldSlugSource(field));

const hasSlugValue = (slug) => typeof slug === 'string' ? slug.trim().length > 0 : Boolean(slug);

const ensureSlugInField = (field) => {
  if (hasSlugValue(field.slug)) return field;

  const generatedSlug = generateFieldSlug(field);
  if (generatedSlug) {
    return { ...field, slug: generatedSlug };
  }

  if (Object.prototype.hasOwnProperty.call(field, 'slug')) {
    const fieldWithoutSlug = { ...field };
    delete fieldWithoutSlug.slug;
    return fieldWithoutSlug;
  }

  return field;
};

// Utility function to ensure all fields have slugs
const ensureSlugsInSteps = (stepsArray) => {
  return stepsArray.map(step => ({
    ...step,
    fields: step.fields.map(ensureSlugInField)
  }));
};

const FIELD_TYPES = [
  { type: 'text', label: 'Text Input', icon: '📝' },
  { type: 'email', label: 'Email', icon: '📧' },
  { type: 'phone', label: 'Phone Number', icon: '📞' },
  { type: 'number', label: 'Number', icon: '🔢' },
  { type: 'textarea', label: 'Long Text', icon: '📄' },
  { type: 'select', label: 'Dropdown', icon: '📋' },
  { type: 'radio', label: 'Single Select', icon: '⚪' },
  { type: 'checkbox', label: 'Multi Select', icon: '☑️' },
  { type: 'date', label: 'Date', icon: '📅' },
  { type: 'hidden', label: 'Hidden Field', icon: '🔒' },
  { type: 'heading', label: 'Heading', icon: 'H' },
  { type: 'paragraph', label: 'Paragraph', icon: '¶' },
  { type: 'image', label: 'Image', icon: '🖼' },
];

const CONTENT_FIELD_TYPES = ['heading', 'paragraph', 'image'];

// ── Image field editor: handles file upload (drag-and-drop / file picker) and URL mode ──
function ImageFieldEditor({ imageUrl, altText, alignment, onUpdate }) {
  const [mode, setMode] = useState(imageUrl && !imageUrl.startsWith('blob:') ? 'url' : 'upload');
  const [urlInput, setUrlInput] = useState(imageUrl || '');
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  const handleFileUpload = async (file) => {
    if (!file || !file.type.startsWith('image/')) {
      setUploadError('Please select a valid image file.');
      return;
    }
    setUploading(true);
    setUploadError(null);
    setUploadProgress(0);

    // Optimistic local preview
    const localUrl = URL.createObjectURL(file);
    onUpdate({ imageUrl: localUrl });

    try {
      // Step 1: Get signed upload URL
      setUploadProgress(10);
      const ext = file.name.split('.').pop().toLowerCase();
      const urlRes = await apiRequest('/media/upload-url', {
        method: 'POST',
        body: JSON.stringify({
          file_extension: ext,
          file_type: file.type,
          file_size: file.size,
        }),
      });
      if (!urlRes.ok) {
        const err = await urlRes.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to get upload URL');
      }
      const { upload_url, file_path, public_url } = await urlRes.json();

      // Step 2: PUT to storage
      setUploadProgress(40);
      const storageRes = await fetch(upload_url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });
      if (!storageRes.ok) throw new Error('Failed to upload to storage');

      // Step 3: Create media record
      setUploadProgress(80);
      await apiRequest('/media', {
        method: 'POST',
        body: JSON.stringify({
          filename: file.name,
          original_filename: file.name,
          storage_path: file_path,
          public_url,
          file_type: file.type,
          file_size: file.size,
        }),
      });

      setUploadProgress(100);
      onUpdate({ imageUrl: public_url });
      setUrlInput(public_url);
    } catch (err) {
      setUploadError(err.message || 'Upload failed. Please try again.');
      onUpdate({ imageUrl: '' });
    } finally {
      URL.revokeObjectURL(localUrl);
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) handleFileUpload(file);
    // Reset so the same file can be re-selected
    e.target.value = '';
  };

  const handleUrlApply = () => {
    onUpdate({ imageUrl: urlInput });
  };

  const handleClear = () => {
    onUpdate({ imageUrl: '' });
    setUrlInput('');
  };

  return (
    <div className="space-y-3">
      {/* Mode toggle */}
      <div className="flex rounded-md overflow-hidden border border-gray-300 dark:border-gray-600">
        <button
          type="button"
          onClick={() => setMode('upload')}
          className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors ${
            mode === 'upload'
              ? 'bg-brand-500 text-white'
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          <Upload className="w-3.5 h-3.5" />
          Upload
        </button>
        <button
          type="button"
          onClick={() => setMode('url')}
          className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm font-medium border-l border-gray-300 dark:border-gray-600 transition-colors ${
            mode === 'url'
              ? 'bg-brand-500 text-white'
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          <LinkIcon className="w-3.5 h-3.5" />
          URL
        </button>
      </div>

      {mode === 'upload' ? (
        /* ── Upload zone ── */
        <div>
          <div
            onDrop={handleDrop}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
            onClick={() => !uploading && fileInputRef.current?.click()}
            className={`relative flex flex-col items-center justify-center gap-2 p-5 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
              dragOver
                ? 'border-brand-400 bg-brand-50 dark:bg-brand-900/20'
                : 'border-gray-300 dark:border-gray-600 hover:border-brand-400 hover:bg-gray-50 dark:hover:bg-gray-800'
            } ${uploading ? 'pointer-events-none opacity-70' : ''}`}
          >
            {uploading ? (
              <>
                <Loader2 className="w-7 h-7 text-brand-500 animate-spin" />
                <p className="text-sm text-gray-600 dark:text-gray-400">Uploading… {uploadProgress}%</p>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 mt-1">
                  <div
                    className="bg-brand-500 h-1.5 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </>
            ) : imageUrl && !imageUrl.startsWith('blob:') ? (
              /* Uploaded image preview */
              <>
                <img
                  src={imageUrl}
                  alt={altText || ''}
                  className="max-h-32 max-w-full rounded object-contain"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400">Click or drag to replace</p>
              </>
            ) : (
              <>
                <Upload className="w-7 h-7 text-gray-400" />
                <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                  <span className="font-medium text-brand-600 dark:text-brand-400">Click to upload</span> or drag &amp; drop
                </p>
                <p className="text-xs text-gray-400">PNG, JPG, GIF, WebP</p>
              </>
            )}
          </div>
          {uploadError && (
            <p className="mt-1.5 text-xs text-red-600 dark:text-red-400">{uploadError}</p>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
            className="hidden"
            onChange={handleFileInput}
          />
        </div>
      ) : (
        /* ── URL mode ── */
        <div className="space-y-2">
          <div className="flex gap-2">
            <input
              type="url"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleUrlApply(); } }}
              placeholder="https://example.com/image.jpg"
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-800 dark:text-gray-100"
            />
            <button
              type="button"
              onClick={handleUrlApply}
              className={`${buttonStyles.primary} ${buttonStyles.small} whitespace-nowrap`}
            >
              Apply
            </button>
          </div>
          {imageUrl && (
            <div className="relative rounded-md overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-2">
              <img
                src={imageUrl}
                alt={altText || ''}
                className="max-h-32 w-auto mx-auto object-contain block"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <button
                type="button"
                onClick={handleClear}
                className="absolute top-1 right-1 p-0.5 rounded-full bg-white dark:bg-gray-900 text-gray-500 hover:text-red-500 border border-gray-200 dark:border-gray-700"
                title="Remove image"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Alt text */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Alt Text</label>
        <input
          type="text"
          value={altText || ''}
          onChange={(e) => onUpdate({ altText: e.target.value })}
          placeholder="Describe the image (for accessibility)"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-800 dark:text-gray-100"
        />
      </div>

      {/* Alignment */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Alignment</label>
        <div className="flex rounded-md overflow-hidden border border-gray-300 dark:border-gray-600">
          {['left', 'center', 'right'].map((align) => (
            <button
              key={align}
              type="button"
              onClick={() => onUpdate({ alignment: align })}
              className={`flex-1 py-1.5 text-sm font-medium capitalize transition-colors ${
                align !== 'left' ? 'border-l border-gray-300 dark:border-gray-600' : ''
              } ${
                (alignment || 'center') === align
                  ? 'bg-brand-500 text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {align}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function SortableField({ field, onUpdate, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const [isEditing, setIsEditing] = useState(false);
  const [localField, setLocalField] = useState(field);

  const handleSave = () => {
    // Ensure slug is set before saving
    const fieldToSave = ensureSlugInField(localField);
    onUpdate(fieldToSave);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setLocalField(field);
    setIsEditing(false);
  };

  const updateLocalField = (updates, { refreshSlug = false } = {}) => {
    setLocalField(prevField => {
      const nextField = { ...prevField, ...updates };
      if (refreshSlug && !prevField.slugManuallySet) {
        nextField.slug = generateFieldSlug(nextField);
      }
      return nextField;
    });
  };

  const updateSlug = (slug) => {
    updateLocalField({
      slug,
      slugManuallySet: slug.trim().length > 0
    });
  };


  return (
    <div ref={setNodeRef} style={style} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-4">
  <div className="flex items-center justify-between mb-2">
  <div className="flex items-center space-x-2">
          <button {...attributes} {...listeners} className="text-gray-400 hover:text-gray-600">
            <GripVertical className="w-4 h-4" />
          </button>
          <span className="text-sm text-gray-500 dark:text-gray-400">{FIELD_TYPES.find(t => t.type === field.type)?.label}</span>
        </div>
  <div className="flex items-center space-x-2">
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="text-gray-400 hover:text-gray-600"
          >
            <Settings className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(field.id)}
            className="text-gray-400 hover:text-red-500"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {isEditing ? (
  <div className="space-y-4">

          {/* ── Heading field editing ── */}
          {field.type === 'heading' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Heading Text</label>
                <input
                  type="text"
                  value={localField.content || ''}
                  onChange={(e) => updateLocalField({ content: e.target.value }, { refreshSlug: true })}
                  placeholder="Enter heading text"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-800 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Heading Level</label>
                <select
                  value={localField.headingLevel || 'h2'}
                  onChange={(e) => updateLocalField({ headingLevel: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-800 dark:text-gray-100"
                >
                  <option value="h1">H1 — Large heading</option>
                  <option value="h2">H2 — Medium heading</option>
                  <option value="h3">H3 — Small heading</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Slug
                  <span className="text-xs text-gray-500 ml-1">(URL-friendly identifier for API responses)</span>
                </label>
                <input
                  type="text"
                  value={localField.slug || ''}
                  onChange={(e) => updateSlug(e.target.value)}
                  placeholder="auto-generated-from-content"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-800 dark:text-gray-100"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Used in API responses when include_readable=true. Leave empty to auto-generate from content.
                </p>
              </div>
            </>
          )}

          {/* ── Paragraph field editing ── */}
          {field.type === 'paragraph' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Paragraph Text</label>
                <textarea
                  value={localField.content || ''}
                  onChange={(e) => updateLocalField({ content: e.target.value }, { refreshSlug: true })}
                  placeholder="Enter paragraph text"
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-800 dark:text-gray-100 resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Slug
                  <span className="text-xs text-gray-500 ml-1">(URL-friendly identifier for API responses)</span>
                </label>
                <input
                  type="text"
                  value={localField.slug || ''}
                  onChange={(e) => updateSlug(e.target.value)}
                  placeholder="auto-generated-from-content"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-800 dark:text-gray-100"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Used in API responses when include_readable=true. Leave empty to auto-generate from content.
                </p>
              </div>
            </>
          )}

          {/* ── Image field editing ── */}
          {field.type === 'image' && (
            <>
              <ImageFieldEditor
                imageUrl={localField.imageUrl}
                altText={localField.altText}
                alignment={localField.alignment}
                onUpdate={(updates) => updateLocalField(updates, {
                  refreshSlug: Object.prototype.hasOwnProperty.call(updates, 'altText')
                })}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Slug
                  <span className="text-xs text-gray-500 ml-1">(URL-friendly identifier for API responses)</span>
                </label>
                <input
                  type="text"
                  value={localField.slug || ''}
                  onChange={(e) => updateSlug(e.target.value)}
                  placeholder="auto-generated-from-alt-text"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-800 dark:text-gray-100"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Used in API responses when include_readable=true. Leave empty to auto-generate from alt text.
                </p>
              </div>
            </>
          )}

          {/* ── Standard input field editing ── */}
          {!CONTENT_FIELD_TYPES.includes(field.type) && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Label</label>
                <input
                  type="text"
                  value={localField.label}
                  onChange={(e) => updateLocalField({ label: e.target.value }, { refreshSlug: true })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-800 dark:text-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                  Slug 
                  <span className="text-xs text-gray-500 ml-1">(URL-friendly identifier)</span>
                </label>
                <input
                  type="text"
                  value={localField.slug || ''}
                  onChange={(e) => updateSlug(e.target.value)}
                  placeholder="auto-generated-from-label"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-800 dark:text-gray-100"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Used in API responses and as the URL query parameter for pre-filling this field. E.g. <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">?{localField.slug || 'field-slug'}=value</code>
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Placeholder</label>
                <input
                  type="text"
                  value={localField.placeholder || ''}
                  onChange={(e) => updateLocalField({ placeholder: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-800 dark:text-gray-100"
                />
              </div>

              {field.type === 'hidden' && (
                <div className="space-y-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                      Hidden Field Configuration
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                      Hidden fields are not visible to users but their values are included in form submissions.
                    </p>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name={`hidden-config-${field.id}`}
                            checked={!localField.useQueryParam}
                            onChange={() => updateLocalField({ useQueryParam: false })}
                            className="h-4 w-4 text-brand-600 focus:ring-brand-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-200">Static Value</span>
                        </label>
                        {!localField.useQueryParam && (
                          <input
                            type="text"
                            value={localField.defaultValue || ''}
                            onChange={(e) => updateLocalField({ defaultValue: e.target.value })}
                            placeholder="Enter static value"
                            className="mt-2 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-800 dark:text-gray-100"
                          />
                        )}
                      </div>

                      <div>
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name={`hidden-config-${field.id}`}
                            checked={localField.useQueryParam === true}
                            onChange={() => updateLocalField({ useQueryParam: true })}
                            className="h-4 w-4 text-brand-600 focus:ring-brand-500"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-200">Query Parameter</span>
                        </label>
                        {localField.useQueryParam && (
                          <div className="mt-2 space-y-2">
                            <input
                              type="text"
                              value={localField.queryParamName || ''}
                              onChange={(e) => updateLocalField({ queryParamName: e.target.value })}
                              placeholder="Enter query parameter name (e.g., utm_source)"
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-800 dark:text-gray-100"
                            />
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              The field will be populated from the URL query parameter. Works in iframes too.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {(['select', 'radio', 'checkbox'].includes(field.type)) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">Options (one per line)</label>
                  <textarea
                    value={(localField.options || []).join('\n')}
                    onChange={(e) => updateLocalField({ options: e.target.value.split('\n').filter(o => o.trim()) })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-800 dark:text-gray-100"
                    rows="4"
                  />
                </div>
              )}

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id={`required-${field.id}`}
                  checked={localField.required || false}
                  onChange={(e) => updateLocalField({ required: e.target.checked })}
                  className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-gray-300 rounded"
                />
                <label htmlFor={`required-${field.id}`} className="ml-2 block text-sm text-gray-900 dark:text-gray-200">
                  Required field
                </label>
              </div>

              {field.type !== 'hidden' && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id={`prefill-locked-${field.id}`}
                    checked={localField.prefillLocked || false}
                    onChange={(e) => updateLocalField({ prefillLocked: e.target.checked })}
                    className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-gray-300 rounded"
                  />
                  <div className="ml-2">
                    <label htmlFor={`prefill-locked-${field.id}`} className="block text-sm text-gray-900 dark:text-gray-200">
                      Lock pre-filled value
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      When pre-filled via URL, the value will be read-only and cannot be changed by the respondent.
                    </p>
                  </div>
                </div>
              )}

              {field.type === 'phone' && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id={`verification-${field.id}`}
                    checked={localField.requireVerification || false}
                    onChange={(e) => updateLocalField({ requireVerification: e.target.checked })}
                    className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-gray-300 rounded"
                  />
                  <label htmlFor={`verification-${field.id}`} className="ml-2 block text-sm text-gray-900 dark:text-gray-200">
                    Require phone verification (SMS code)
                  </label>
                </div>
              )}
            </>
          )}

          <div className="flex justify-end space-x-2">
            <button
              onClick={handleCancel}
              className={`${buttonStyles.outline} ${buttonStyles.small}`}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className={`${buttonStyles.primary} ${buttonStyles.small}`}
            >
              Save
            </button>
          </div>
        </div>
      ) : (
        <div>
          {field.type === 'heading' && (
            <p className="font-semibold text-gray-900 dark:text-gray-100 truncate">
              {field.headingLevel?.toUpperCase() || 'H2'}: {field.content || '(empty heading)'}
            </p>
          )}
          {field.type === 'paragraph' && (
            <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
              {field.content || '(empty paragraph)'}
            </p>
          )}
          {field.type === 'image' && (
            <div className="flex items-center gap-3">
              {field.imageUrl ? (
                <img
                  src={field.imageUrl}
                  alt={field.altText || ''}
                  className="h-10 w-10 object-cover rounded border border-gray-200 dark:border-gray-700 flex-shrink-0"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div className="h-10 w-10 rounded border border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center flex-shrink-0 text-gray-400 text-xs">
                  IMG
                </div>
              )}
              <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                {field.imageUrl ? 'Image set' : 'No image — click to add'}
              </p>
            </div>
          )}
          {!CONTENT_FIELD_TYPES.includes(field.type) && (
            <>
              <h4 className="font-medium text-gray-900 dark:text-gray-100">{field.label}</h4>
              {field.placeholder && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Placeholder: {field.placeholder}</p>
              )}
              {field.type === 'hidden' && (
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {field.useQueryParam ? (
                    <p>Query Parameter: <span className="font-mono bg-gray-100 dark:bg-gray-800 px-1 rounded">{field.queryParamName || '(not set)'}</span></p>
                  ) : (
                    <p>Static Value: <span className="font-mono bg-gray-100 dark:bg-gray-800 px-1 rounded">{field.defaultValue || '(empty)'}</span></p>
                  )}
                </div>
              )}
            </>
          )}
          <div className="mt-2 flex flex-wrap gap-2">
            {field.required && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200">
                Required
              </span>
            )}
            {field.type === 'phone' && field.requireVerification && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-brand-100 text-brand-800 dark:bg-brand-900 dark:text-brand-200">
                Verification Required
              </span>
            )}
            {field.type === 'hidden' && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                Hidden Field
              </span>
            )}
            {field.type !== 'hidden' && field.prefillLocked && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                Pre-fill Locked
              </span>
            )}
            {field.slug && field.type !== 'hidden' && !CONTENT_FIELD_TYPES.includes(field.type) && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400 font-mono">
                ?{field.slug}=…
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============ Responsive Breakpoint Config ============
const FORM_BREAKPOINTS = [
  { id: 'base', label: 'Mobile', width: 375, icon: Smartphone },
  { id: 'tablet', label: 'Tablet', width: 768, icon: Tablet },
  { id: 'desktop', label: 'Desktop', width: 1200, icon: Monitor },
];

function FormBreakpointToggle({ activeBreakpoint, onChange }) {
  return (
    <div className="inline-flex rounded-md shadow-sm" role="group">
      {FORM_BREAKPOINTS.map((bp, index) => {
        const isActive = activeBreakpoint === bp.id;
        const Icon = bp.icon;
        return (
          <button
            key={bp.id}
            type="button"
            onClick={() => onChange(bp.id)}
            className={`p-1.5 inline-flex items-center justify-center border border-gray-300 dark:border-gray-600 transition-colors duration-150 ${
              index === 0 ? 'rounded-l-md' : ''
            } ${index === FORM_BREAKPOINTS.length - 1 ? 'rounded-r-md' : ''} ${
              index > 0 ? '-ml-px' : ''
            } ${
              isActive
                ? 'bg-brand-500 text-white border-brand-500 z-10'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            } focus:z-10 focus:outline-none`}
            title={`${bp.label} (${bp.width}px)`}
          >
            <Icon className="w-4 h-4" />
          </button>
        );
      })}
    </div>
  );
}

// ============ Form Preview Iframe ============
function FormPreviewIframe({ form, steps, settings, activeBreakpoint, currentStep }) {
  const iframeRef = useRef(null);
  const containerRef = useRef(null);
  const [iframeReady, setIframeReady] = useState(false);
  const [iframeHeight, setIframeHeight] = useState(600);
  const [autoZoom, setAutoZoom] = useState(1);
  const [manualZoom, setManualZoom] = useState(null); // null = use auto zoom

  const breakpointWidth = FORM_BREAKPOINTS.find(bp => bp.id === activeBreakpoint)?.width || 1200;
  const effectiveZoom = manualZoom !== null ? manualZoom : autoZoom;

  // Track container size and calculate auto-zoom
  useEffect(() => {
    if (!containerRef.current) return;
    const updateSize = () => {
      const el = containerRef.current;
      if (!el) return;
      const w = el.clientWidth;
      // Auto-zoom: scale down so the breakpoint canvas fits within the container with padding
      const availableWidth = w - 48; // 24px padding on each side
      if (availableWidth > 0 && breakpointWidth > availableWidth) {
        setAutoZoom(Math.max(0.25, availableWidth / breakpointWidth));
      } else {
        setAutoZoom(1);
      }
    };
    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [breakpointWidth]);

  // Reset manual zoom when breakpoint changes
  useEffect(() => {
    setManualZoom(null);
  }, [activeBreakpoint]);

  const handleZoomIn = () => {
    const current = manualZoom !== null ? manualZoom : autoZoom;
    setManualZoom(Math.min(2, current + 0.1));
  };
  const handleZoomOut = () => {
    const current = manualZoom !== null ? manualZoom : autoZoom;
    setManualZoom(Math.max(0.25, current - 0.1));
  };
  const handleZoomReset = () => {
    setManualZoom(null);
  };

  // Listen for messages from iframe
  useEffect(() => {
    const handleMessage = (event) => {
      if (!event.data || !event.data.type) return;
      switch (event.data.type) {
        case 'PREVIEW_READY':
          setIframeReady(true);
          break;
        case 'PREVIEW_HEIGHT':
          if (event.data.height) {
            setIframeHeight(Math.max(400, event.data.height));
          }
          break;
        default:
          break;
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Send data to iframe when it's ready or data changes
  const sendDataToIframe = useCallback(() => {
    if (!iframeRef.current?.contentWindow || !iframeReady) return;
    iframeRef.current.contentWindow.postMessage({
      type: 'UPDATE_FORM_DATA',
      form: form ? { title: form.title, description: form.description } : null,
      steps: steps,
      settings: settings,
    }, '*');
  }, [form, steps, settings, iframeReady]);

  useEffect(() => {
    sendDataToIframe();
  }, [sendDataToIframe]);

  // Send breakpoint changes
  useEffect(() => {
    if (!iframeRef.current?.contentWindow || !iframeReady) return;
    iframeRef.current.contentWindow.postMessage({
      type: 'SET_BREAKPOINT',
      breakpoint: activeBreakpoint,
    }, '*');
  }, [activeBreakpoint, iframeReady]);

  // Send current step changes
  useEffect(() => {
    if (!iframeRef.current?.contentWindow || !iframeReady) return;
    iframeRef.current.contentWindow.postMessage({
      type: 'SET_CURRENT_STEP',
      currentStep: currentStep,
    }, '*');
  }, [currentStep, iframeReady]);

  // The iframe renders at the full breakpoint width (e.g. 1200px for desktop).
  // We scale it down visually with CSS transform so it fits the container.
  // The scaled height = the real iframe content height, so the wrapper uses
  // the scaled dimensions to avoid overflow / blank space.
  const scaledWidth = breakpointWidth * effectiveZoom;
  const scaledHeight = iframeHeight * effectiveZoom;

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Zoom controls bar */}
      <div className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-gray-50 dark:bg-gray-850 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={handleZoomOut}
          className="p-1 rounded text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          title="Zoom out"
        >
          <ZoomOut className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={handleZoomReset}
          className="px-2 py-0.5 rounded text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors min-w-[3.5rem] text-center tabular-nums"
          title="Reset zoom"
        >
          {Math.round(effectiveZoom * 100)}%
        </button>
        <button
          onClick={handleZoomIn}
          className="p-1 rounded text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
          title="Zoom in"
        >
          <ZoomIn className="w-3.5 h-3.5" />
        </button>
      </div>
      {/* Preview canvas */}
      <div ref={containerRef} className="flex-1 overflow-auto bg-gray-100 dark:bg-gray-800">
        <div className="flex justify-center" style={{ padding: '24px' }}>
          {/* Wrapper sized to the scaled dimensions so the scrollable area is correct */}
          <div
            style={{
              width: `${scaledWidth}px`,
              height: `${scaledHeight}px`,
              flexShrink: 0,
            }}
          >
            {/* Inner element at full breakpoint width, scaled down with transform */}
            <div
              style={{
                width: `${breakpointWidth}px`,
                height: `${iframeHeight}px`,
                transform: `scale(${effectiveZoom})`,
                transformOrigin: 'top left',
                transition: 'transform 0.3s ease, width 0.3s ease',
              }}
            >
              <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg overflow-hidden" style={{ width: '100%', height: '100%' }}>
                <iframe
                  ref={iframeRef}
                  src={`/forms/${form?.id || 'new'}/preview`}
                  title="Form Preview"
                  className="border-0"
                  style={{
                    width: `${breakpointWidth}px`,
                    height: `${iframeHeight}px`,
                    display: 'block',
                    pointerEvents: 'none', // Let scroll events pass through to the parent container
                  }}
                  sandbox="allow-scripts allow-same-origin"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FormBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState([]);
  const [copiedLink, setCopiedLink] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [initialOrganizationId, setInitialOrganizationId] = useState(null);
  const [versions, setVersions] = useState([]);
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [copiedSnippet, setCopiedSnippet] = useState('');
  const [activeTab, setActiveTab] = useState('builder');
  const [editingStepIndex, setEditingStepIndex] = useState(null);
  const [showShareDropdown, setShowShareDropdown] = useState(false);
  const [activeBreakpoint, setActiveBreakpoint] = useState('desktop');
  const [rightPanelTab, setRightPanelTab] = useState('fields'); // 'fields' or 'theme'
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [templateCategory, setTemplateCategory] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [copiedPrefillUrl, setCopiedPrefillUrl] = useState(false);
  const { showSuccess, showError } = useToast();
  const { organization } = useAuth();
  const shareDropdownRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Load form from backend
  useEffect(() => {
    fetchForm();
    fetchVersions();
    // eslint-disable-next-line
  }, [id]);

  // Handle organization changes
  useEffect(() => {
    if (organization && initialOrganizationId && organization.id !== initialOrganizationId) {
      // Organization has changed
      const message = hasUnsavedChanges 
        ? 'You switched organizations. Any unsaved changes will be lost. You will be redirected to the forms list.'
        : 'You switched organizations. You will be redirected to the forms list.';
      
      if (window.confirm(message)) {
        navigate('/forms');
      }
    }
  }, [organization, initialOrganizationId, hasUnsavedChanges, navigate]);

  // Handle clicking outside share dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (shareDropdownRef.current && !shareDropdownRef.current.contains(event.target)) {
        setShowShareDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const updateSettings = (key, value) => {
    setHasUnsavedChanges(true); // Mark as having unsaved changes
    const updatedSettings = { ...(form.settings || {}), [key]: value };
    const updatedForm = { ...form, settings: updatedSettings };
    setForm(updatedForm);
  };

  const updateTheme = (newTheme) => {
    setHasUnsavedChanges(true);
    const updatedSettings = { ...(form.settings || {}), theme: newTheme };
    // Also sync primary color to section_button_color for backward compat
    if (newTheme.primaryColor) {
      updatedSettings.section_button_color = newTheme.primaryColor;
    }
    const updatedForm = { ...form, settings: updatedSettings };
    setForm(updatedForm);
  };

  const fetchForm = async () => {
    try {
      const response = await apiRequest(`/forms/${id}`);
      const data = await response.json();
      setForm(data);
      
      // Track the initial organization ID when form is loaded
      if (organization && !initialOrganizationId) {
        setInitialOrganizationId(organization.id);
      }
      
      const formSteps = data.fields?.steps || [];
      if (formSteps.length === 0) {
        // Create initial step if none exist
        const initialStep = {
          id: uuidv4(),
          title: 'Step 1',
          fields: []
        };
        setSteps([initialStep]);
      } else {
        // Ensure all fields have slugs (for backward compatibility)
        const stepsWithSlugs = ensureSlugsInSteps(formSteps);
        setSteps(stepsWithSlugs);
      }
    } catch (error) {
      console.error('Error fetching form:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVersions = async () => {
    try {
      const response = await apiRequest(`/forms/${id}/versions`);
      const data = await response.json();
      setVersions(data);
    } catch (error) {
      console.error('Error fetching versions:', error);
    }
  };

  const deleteStep = (stepIdx) => {
    if (steps.length <= 1) return;
    const deletedStepId = steps[stepIdx].id;
    let updatedSteps = steps.filter((_, idx) => idx !== stepIdx);

    // Clean up condition references to the deleted step (target refs AND field refs)
    const deletedFieldIds = new Set((steps[stepIdx].fields || []).map(f => f.id));
    updatedSteps = updatedSteps.map(s => {
      // Remove conditions targeting the deleted step or referencing its fields
      const newConditions = (s.conditions || []).filter(c =>
        c.targetStepId !== deletedStepId && !deletedFieldIds.has(c.fieldId)
      );
      // Clear default next step if it pointed to the deleted step
      const newDefault = s.defaultNextStepId === deletedStepId ? null : s.defaultNextStepId;

      const conditionsChanged = newConditions.length !== (s.conditions || []).length;
      const defaultChanged = newDefault !== s.defaultNextStepId;

      if (conditionsChanged || defaultChanged) {
        return { ...s, conditions: newConditions, defaultNextStepId: newDefault };
      }
      return s;
    });

    setSteps(updatedSteps);

    // If current step is removed or after last, adjust pointer
    let newCurrent = currentStep;
    if (stepIdx === currentStep && currentStep > 0) newCurrent -= 1;
    else if (currentStep >= updatedSteps.length) newCurrent = updatedSteps.length - 1;
    setCurrentStep(newCurrent);

    setHasUnsavedChanges(true); // Mark as having unsaved changes
  };

  const addStep = () => {
    const newStep = {
      id: uuidv4(),
      title: `Step ${steps.length + 1}`,
      fields: []
    };
    const newSteps = [...steps, newStep];
    setSteps(newSteps);
    setCurrentStep(newSteps.length - 1);
    setHasUnsavedChanges(true); // Mark as having unsaved changes
  };

  // Manual save function - only called when user clicks save
  const saveForm = async (nextSteps = steps, newSettings = form.settings) => {
    setSaving(true);
    try {
      const updatedForm = {
        ...form,
        fields: { steps: nextSteps },
        settings: newSettings,
        updated_at: new Date().toISOString()
      };
      const response = await apiRequest(`/forms/${id}`, {
        method: 'PUT',
        body: JSON.stringify(updatedForm),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save form');
      }
      
      // Get the updated form data with the new version number
      const savedFormData = await response.json();
      setForm(savedFormData);
      
      setHasUnsavedChanges(false); // Clear unsaved changes after successful save
      showSuccess('Form saved successfully!');
      // Refresh versions after save
      fetchVersions();
    } catch (error) {
      console.error('Error saving form:', error);
      
      // Show specific error message from backend validation
      if (error.message.includes('slug') || error.message.includes('Slug')) {
        showError(`Validation Error: ${error.message}`);
      } else if (error.message.includes('duplicate') || error.message.includes('conflict')) {
        showError(`Field Conflict: ${error.message}`);
      } else {
        showError(`Failed to save form: ${error.message}`);
      }
      
      // Keep hasUnsavedChanges as true if save failed
    } finally {
      setSaving(false);
    }
  };

  // Cmd+S / Ctrl+S → save
  useKeyboardShortcut('s', () => { if (!saving) saveForm(); }, { mod: true, allowInInput: true }, [saving, steps, form]);

  // Operations that modify the steps array or field arrays:

  const addField = (fieldType) => {
    const fieldLabel = `New ${FIELD_TYPES.find(t => t.type === fieldType)?.label}`;
    const isContentField = CONTENT_FIELD_TYPES.includes(fieldType);
    const newField = {
      id: uuidv4(),
      type: fieldType,
      label: fieldLabel,
      required: false,
      placeholder: isContentField ? undefined : '',
      options: fieldType === 'select' || fieldType === 'radio' || fieldType === 'checkbox' ? ['Option 1', 'Option 2'] : null,
      requireVerification: fieldType === 'phone' ? false : undefined,
      // Hidden field specific properties
      useQueryParam: fieldType === 'hidden' ? false : undefined,
      defaultValue: fieldType === 'hidden' ? '' : undefined,
      queryParamName: fieldType === 'hidden' ? '' : undefined,
      // Heading / paragraph content
      content: fieldType === 'heading' ? 'New Heading' : fieldType === 'paragraph' ? 'New paragraph text.' : undefined,
      headingLevel: fieldType === 'heading' ? 'h2' : undefined,
      // Image field
      imageUrl: fieldType === 'image' ? '' : undefined,
      altText: fieldType === 'image' ? '' : undefined,
      alignment: fieldType === 'image' ? 'center' : undefined,
    };
    const updatedSteps = [...steps];
    updatedSteps[currentStep].fields.push(ensureSlugInField(newField));
    setSteps(updatedSteps);
    setHasUnsavedChanges(true); // Mark as having unsaved changes
  };

  const updateField = (fieldId, updates) => {
    const updatedSteps = [...steps];
    const fieldIndex = updatedSteps[currentStep].fields.findIndex(f => f.id === fieldId);
    if (fieldIndex !== -1) {
      updatedSteps[currentStep].fields[fieldIndex] = { ...updatedSteps[currentStep].fields[fieldIndex], ...updates };
      setSteps(updatedSteps);
      setHasUnsavedChanges(true); // Mark as having unsaved changes
    }
  };

  const deleteField = (fieldId) => {
    const updatedSteps = [...steps];
    updatedSteps[currentStep].fields = updatedSteps[currentStep].fields.filter(f => f.id !== fieldId);
    setSteps(updatedSteps);
    setHasUnsavedChanges(true); // Mark as having unsaved changes
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const updatedSteps = [...steps];
      const fields = updatedSteps[currentStep].fields;
      const oldIndex = fields.findIndex(f => f.id === active.id);
      const newIndex = fields.findIndex(f => f.id === over.id);
      updatedSteps[currentStep].fields = arrayMove(fields, oldIndex, newIndex);
      setSteps(updatedSteps);
      setHasUnsavedChanges(true); // Mark as having unsaved changes
    }
  };

  const updateStepTitle = (stepIndex, title) => {
    const updatedSteps = [...steps];
    updatedSteps[stepIndex].title = title;
    setSteps(updatedSteps);
    setHasUnsavedChanges(true); // Mark as having unsaved changes
  };

  const updateStepData = (stepIndex, updatedStep) => {
    const updatedSteps = [...steps];
    updatedSteps[stepIndex] = updatedStep;
    setSteps(updatedSteps);
    setHasUnsavedChanges(true);
  };

  const copyPublicLink = async () => {
    const publicUrl = `${window.location.origin}/public/forms/${id}`;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopiedLink(true);
      showSuccess('Public form link copied to clipboard!');
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      showError('Failed to copy link');
    }
  };

  const copyEmbedSnippet = async (snippet, type) => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopiedSnippet(type);
      showSuccess('Embed code copied to clipboard!');
      setTimeout(() => setCopiedSnippet(''), 2000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      showError('Failed to copy embed code');
    }
  };

  const handleSaveAsTemplate = async (e) => {
    e.preventDefault();
    if (!templateName.trim() || !templateCategory.trim()) return;
    setSavingTemplate(true);
    try {
      const response = await apiRequest('/form-templates/', {
        method: 'POST',
        body: JSON.stringify({
          name: templateName.trim(),
          description: templateDescription.trim() || null,
          category: templateCategory.trim(),
          fields: { steps },
          settings: form.settings || {},
        }),
      });
      if (response.ok) {
        showSuccess('Form saved as template!');
        setShowSaveTemplateModal(false);
        setTemplateName('');
        setTemplateDescription('');
        setTemplateCategory('');
      } else {
        const err = await response.json();
        showError(err.detail || 'Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      showError('Failed to save template');
    } finally {
      setSavingTemplate(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  if (!form) {
    return <div className="text-center">Form not found</div>;
  }

  const currentStepData = steps[currentStep] || { fields: [] };

  return (
    <div className="flex-1 flex flex-col h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/forms" className="flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Forms
            </Link>
            <div className="h-5 w-px bg-gray-200 dark:bg-gray-700" />
            <div>
              <h1 className="text-base font-semibold text-gray-900 dark:text-gray-100 leading-tight">{form.title}</h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">Version {form.current_version || 1} &middot; {currentStepData.fields.length} field{currentStepData.fields.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => saveForm()}
                disabled={saving}
                title={`Save (${getModKey()}+S)`}
                className={`${hasUnsavedChanges ? 'bg-brand-600 hover:bg-brand-700 text-white' : 'bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'} px-4 py-2 rounded-lg font-medium transition-colors inline-flex items-center gap-2`}
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : hasUnsavedChanges ? 'Save Changes' : 'Saved'}
              </button>
              <button
                onClick={() => {
                  setTemplateName(form.title || '');
                  setTemplateDescription(form.description || '');
                  setShowSaveTemplateModal(true);
                }}
                title="Save as Template"
                className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <BookmarkPlus className="w-4 h-4" />
              </button>
              {hasUnsavedChanges && (
                <span className="text-sm text-amber-600 dark:text-amber-400 font-medium">
                  Unsaved changes
                </span>
              )}
              {form.status === 'published' && (
                <>
   
                  
                  {/* Share Dropdown */}
                  <div className="relative" ref={shareDropdownRef}>
                    <button
                      onClick={() => setShowShareDropdown(!showShareDropdown)}
                      className={`${buttonStyles.outline} inline-flex items-center gap-2 dark:border-gray-600 dark:text-gray-200 dark:bg-gray-900`}
                      title="Share your form"
                    >
                      <Share className="w-4 h-4" />
                      Share
                    </button>
                    
                    {showShareDropdown && (
                      <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50">
                        <button
                          onClick={() => {
                            copyPublicLink();
                            setShowShareDropdown(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-3"
                        >
                          {copiedLink ? (
                            <>
                              <Check className="w-4 h-4 text-brand-600" />
                              <div>
                                <div className="font-medium text-brand-600">Link Copied!</div>
                                <div className="text-xs text-gray-500">Public form link</div>
                              </div>
                            </>
                          ) : (
                            <>
                              <Copy className="w-4 h-4" />
                              <div>
                                <div className="font-medium">Copy Public Link</div>
                                <div className="text-xs text-gray-500">Share with respondents</div>
                              </div>
                            </>
                          )}
                        </button>
                        
                        <button
                          onClick={() => {
                            setShowEmbedModal(true);
                            setShowShareDropdown(false);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-3"
                        >
                          <Code className="w-4 h-4" />
                          <div>
                            <div className="font-medium">Embed Form</div>
                            <div className="text-xs text-gray-500">Add to your website</div>
                          </div>
                        </button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Tab Navigation with Responsive Controls */}
  <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
          <div className="px-6 flex items-center justify-between">
            <nav className="flex space-x-8">
              <button
                onClick={() => setActiveTab('builder')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'builder'
                    ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                Builder
              </button>
              <button
                onClick={() => setActiveTab('settings')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'settings'
                    ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                Settings
              </button>
              <button
                onClick={() => setActiveTab('integrations')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'integrations'
                    ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                Integrations
              </button>
              <button
                onClick={() => setActiveTab('versions')}
                className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'versions'
                    ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                Versions
              </button>
            </nav>
            {/* Preview Controls - Only show on builder tab */}
            {activeTab === 'builder' && (
              <div className="flex items-center gap-3">
                <FormBreakpointToggle
                  activeBreakpoint={activeBreakpoint}
                  onChange={setActiveBreakpoint}
                />
                <div className="h-5 w-px bg-gray-200 dark:bg-gray-700" />
                <div className="inline-flex rounded-md shadow-sm" role="group">
                  <button
                    onClick={() => setRightPanelTab('fields')}
                    className={`p-1.5 inline-flex items-center justify-center border transition-colors rounded-l-md ${
                      rightPanelTab === 'fields'
                        ? 'bg-brand-500 text-white border-brand-500 z-10'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                    } focus:z-10 focus:outline-none`}
                    title="Fields & Steps"
                  >
                    <ListTree className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setRightPanelTab('theme')}
                    className={`p-1.5 -ml-px inline-flex items-center justify-center border transition-colors rounded-r-md ${
                      rightPanelTab === 'theme'
                        ? 'bg-brand-500 text-white border-brand-500 z-10'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                    } focus:z-10 focus:outline-none`}
                    title="Theme Customization"
                  >
                    <Palette className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        {activeTab === 'builder' && (
          <div className="flex-1 flex overflow-hidden">
            {/* Left Panel — Field list for active step */}
            <div className="w-[340px] flex-shrink-0 border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex flex-col overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {currentStepData.title}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {currentStepData.fields.length} field{currentStepData.fields.length !== 1 ? 's' : ''} &middot; Drag to reorder
                </p>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                {currentStepData.fields.length === 0 ? (
                  <div className="text-center py-10">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                      <Plus className="w-5 h-5 text-gray-400" />
                    </div>
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No fields yet</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Add fields from the right panel</p>
                  </div>
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext items={currentStepData.fields.map(f => f.id)} strategy={verticalListSortingStrategy}>
                      {currentStepData.fields.map((field) => (
                        <SortableField
                          key={field.id}
                          field={field}
                          onUpdate={(updates) => updateField(field.id, updates)}
                          onDelete={deleteField}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                )}
              </div>
            </div>

            {/* Live Preview Canvas */}
            <FormPreviewIframe
              form={form}
              steps={steps}
              settings={form?.settings || {}}
              activeBreakpoint={activeBreakpoint}
              currentStep={currentStep}
            />

            {/* Right Panel — Steps & Add Fields, or Theme */}
            <div className="w-[300px] flex-shrink-0 border-l border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex flex-col overflow-hidden">
              {rightPanelTab === 'fields' ? (
                <div className="flex-1 overflow-y-auto">
                  {/* Steps */}
                  <div className="p-4 border-b border-gray-200 dark:border-gray-800">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Steps</h3>
                    <div className="space-y-1">
                      {steps.map((step, index) => (
                        <div key={step.id}>
                          <div className="flex items-center space-x-1.5">
                            {editingStepIndex === index ? (
                              <input
                                type="text"
                                value={step.title}
                                onChange={(e) => updateStepTitle(index, e.target.value)}
                                onBlur={() => setEditingStepIndex(null)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === 'Escape') {
                                    setEditingStepIndex(null);
                                  }
                                }}
                                autoFocus
                                className="flex-1 px-2.5 py-1.5 text-sm border border-brand-300 dark:border-brand-700 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 dark:bg-gray-800 dark:text-gray-100"
                              />
                            ) : (
                              <button
                                onClick={() => setCurrentStep(index)}
                                onDoubleClick={() => setEditingStepIndex(index)}
                                className={`flex-1 text-left px-2.5 py-1.5 rounded-md text-sm transition-colors ${
                                  currentStep === index
                                    ? 'bg-brand-50 dark:bg-brand-900/40 text-brand-700 dark:text-brand-200 font-medium'
                                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                                }`}
                                title="Double-click to rename"
                              >
                                <span className="flex items-center gap-1.5">
                                  {step.title}
                                  {(step.conditions?.length > 0 || step.defaultNextStepId) && (
                                    <GitBranch className="w-3 h-3 text-amber-500 dark:text-amber-400 flex-shrink-0" />
                                  )}
                                </span>
                              </button>
                            )}
                            {editingStepIndex !== index && currentStep === index && (
                              <button
                                onClick={() => setEditingStepIndex(index)}
                                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 p-0.5"
                                title="Edit step name"
                              >
                                <Settings className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {steps.length > 1 && editingStepIndex !== index && (
                              <button
                                className="text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 p-0.5"
                                onClick={() => {
                                  if (window.confirm('Delete this step and all its questions?')) deleteStep(index);
                                }}
                                title="Delete Step"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                          {/* Step Condition Editor — shown when step is selected and form has multiple steps */}
                          {currentStep === index && steps.length > 1 && (
                            <StepConditionEditor
                              step={step}
                              stepIndex={index}
                              steps={steps}
                              onChange={(updatedStep) => updateStepData(index, updatedStep)}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={addStep}
                      className="w-full mt-2 flex items-center justify-center px-3 py-1.5 border border-dashed border-gray-300 dark:border-gray-600 rounded-md text-xs text-gray-500 dark:text-gray-400 hover:text-brand-600 dark:hover:text-brand-400 hover:border-brand-300 dark:hover:border-brand-500 transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" />
                      Add Step
                    </button>
                  </div>

                  {/* Add Fields */}
                  <div className="p-4">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">Add Fields</h3>
                    <div className="grid grid-cols-2 gap-1.5">
                      {FIELD_TYPES.map((fieldType) => (
                        <button
                          key={fieldType.type}
                          onClick={() => addField(fieldType.type)}
                          className="flex items-center gap-2 px-2.5 py-2 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-brand-300 dark:hover:border-brand-500 hover:bg-brand-50 dark:hover:bg-brand-900/40 text-xs transition-colors"
                        >
                          <span className="text-sm">{fieldType.icon}</span>
                          <span className="text-gray-700 dark:text-gray-200">{fieldType.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <FormThemePanel
                  theme={form?.settings?.theme || {}}
                  onChange={updateTheme}
                />
              )}
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="flex-1 p-6 overflow-y-auto">
            <div className="max-w-3xl mx-auto">
              <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8">
                <div className="mb-8">
                  <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Form Settings</h2>
                  <p className="text-gray-600 dark:text-gray-400">
                    Configure how your form behaves and looks to respondents.
                  </p>
                </div>

                <div className="space-y-8">
                  {/* Form Behavior Section */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Thank you page</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                          Thank You Message
                        </label>
                        <input
                          type="text"
                          value={form.settings?.thank_you_message || ''}
                          onChange={e => updateSettings('thank_you_message', e.target.value)}
                          placeholder="Thank you for your submission!"
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent dark:bg-gray-800 dark:text-gray-100"
                        />
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                          Message shown to users after form submission
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                          Redirect URL (Optional)
                        </label>
                        <input
                          type="url"
                          value={form.settings?.redirect_url || ''}
                          onChange={e => updateSettings('redirect_url', e.target.value)}
                          placeholder="https://yourdomain.com/thank-you"
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent dark:bg-gray-800 dark:text-gray-100"
                        />
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                          Redirect users to this URL after submission
                        </p>
                      </div>
                    </div>

                    {/* Fields in Redirect URL Configuration */}
                    {form.settings?.redirect_url && (() => {
                      // Get all fields from all steps
                      const allFields = steps.flatMap(step => 
                        (step.fields || [])
                      );
                      
                      if (allFields.length > 0) {
                        return (
                          <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                            <div className="mb-3">
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                                Parse Form Fields into Redirect URL
                              </label>
                              <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                                Select which form fields should be added as query parameters to the redirect URL.
                              </p>
                            </div>
                            
                            <div className="space-y-2">
                              {allFields.map(field => {
                                const selectedFields = form.settings?.redirect_hidden_fields || [];
                                const isSelected = selectedFields.includes(field.id);
                                
                                return (
                                  <label key={field.id} className="flex items-start space-x-3 cursor-pointer p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={(e) => {
                                        const currentFields = form.settings?.redirect_hidden_fields || [];
                                        const newFields = e.target.checked
                                          ? [...currentFields, field.id]
                                          : currentFields.filter(id => id !== field.id);
                                        updateSettings('redirect_hidden_fields', newFields);
                                      }}
                                      className="mt-0.5 h-4 w-4 text-brand-600 focus:ring-brand-500 border-gray-300 rounded"
                                    />
                                    <div className="flex-1">
                                      <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{field.label}</span>
                                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                        {field.slug ? (
                                          <>Will be added as: <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">{field.slug}</code></>
                                        ) : (
                                          <span className="text-amber-600 dark:text-amber-400">⚠️ Missing slug - will be skipped</span>
                                        )}
                                      </div>
                                    </div>
                                  </label>
                                );
                              })}
                            </div>
                            
                            {/* Example Preview */}
                            {(() => {
                              const selectedFields = form.settings?.redirect_hidden_fields || [];
                              const selectedFormFields = allFields.filter(f => selectedFields.includes(f.id) && f.slug);
                              
                              if (selectedFormFields.length > 0) {
                                const baseUrl = form.settings?.redirect_url || '';
                                const params = selectedFormFields.map(f => `${f.slug}=<value>`).join('&');
                                const exampleUrl = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}${params}`;
                                
                                return (
                                  <div className="mt-4 p-3 bg-brand-50 dark:bg-brand-900/20 border border-brand-200 dark:border-brand-800 rounded">
                                    <p className="text-xs font-medium text-brand-800 dark:text-brand-200 mb-1">Example redirect URL:</p>
                                    <code className="text-xs text-brand-700 dark:text-brand-300 break-all">{exampleUrl}</code>
                                  </div>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>

                  {/* Pre-fill URL Helper Section */}
                  <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Pre-fill URL</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      You can pre-populate form fields by adding query parameters to your form URL using each field's slug. Values are URL-decoded automatically and invalid values for typed fields are silently ignored.
                    </p>
                    {(() => {
                      const prefillableFields = steps
                        .flatMap(step => step.fields || [])
                        .filter(f => f.slug && !CONTENT_FIELD_TYPES.includes(f.type) && f.type !== 'hidden');

                      if (prefillableFields.length === 0) {
                        return (
                          <p className="text-sm text-gray-400 dark:text-gray-500 italic">
                            Add some fields to your form to see the pre-fill URL.
                          </p>
                        );
                      }

                      const baseUrl = `${window.location.origin}/public/forms/${id}`;
                      const params = prefillableFields.map(f => `${encodeURIComponent(f.slug)}={${f.label || f.slug}}`).join('&');
                      const sampleUrl = `${baseUrl}?${params}`;

                      return (
                        <div className="space-y-3">
                          <div className="p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                            <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Sample pre-fill URL (replace placeholder values):</p>
                            <code className="text-xs text-gray-600 dark:text-gray-400 break-all block">{sampleUrl}</code>
                          </div>
                          <div className="space-y-1">
                            {prefillableFields.map(f => (
                              <div key={f.id} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                                <code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded font-mono">{f.slug}</code>
                                <span className="text-gray-400">→</span>
                                <span>{f.label || '(unlabelled)'}</span>
                                {f.prefillLocked && <span className="text-amber-600 dark:text-amber-400 text-xs">(locked)</span>}
                              </div>
                            ))}
                          </div>
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                await navigator.clipboard.writeText(sampleUrl);
                                setCopiedPrefillUrl(true);
                                showSuccess('Sample pre-fill URL copied to clipboard!');
                                setTimeout(() => setCopiedPrefillUrl(false), 2000);
                              } catch {
                                showError('Failed to copy URL');
                              }
                            }}
                            className="inline-flex items-center gap-2 px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                          >
                            {copiedPrefillUrl ? <Check className="w-4 h-4 text-brand-600" /> : <Copy className="w-4 h-4" />}
                            {copiedPrefillUrl ? 'Copied!' : 'Copy sample pre-fill URL'}
                          </button>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Disqualification Section */}
                  <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                    <DisqualificationEditor
                      conditions={form.settings?.disqualification_conditions || []}
                      steps={steps}
                      onChange={newConditions => updateSettings('disqualification_conditions', newConditions)}
                    />
                  </div>

                  {/* Appearance Section */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Appearance</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                          Theme Color
                        </label>
                        <input
                          type="text"
                          value={form.settings?.theme_color || ''}
                          onChange={e => updateSettings('theme_color', e.target.value)}
                          placeholder="#A020F0 or indigo-600"
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent dark:bg-gray-800 dark:text-gray-100"
                        />
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                          Primary color for buttons and accents (hex code or Tailwind class)
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                          Section Button Color
                        </label>
                        <input
                          type="text"
                          value={form.settings?.section_button_color || ''}
                          onChange={e => updateSettings('section_button_color', e.target.value)}
                          placeholder="#A020F0 or indigo-600"
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent dark:bg-gray-800 dark:text-gray-100"
                        />
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                          Color for section navigation buttons
                        </p>
                      </div>
                    </div>

                    {/* Step Header Display Options */}
                    <div className="mt-6">
                      <div className="flex items-center">
                        <input
                          id="hide-step-header"
                          type="checkbox"
                          checked={form.settings?.hide_step_header_text || false}
                          onChange={e => updateSettings('hide_step_header_text', e.target.checked)}
                          className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-gray-300 rounded"
                        />
                        <label htmlFor="hide-step-header" className="ml-3 block text-sm font-medium text-gray-700">
                          Hide step header text
                        </label>
                      </div>
                      <p className="mt-1 ml-7 text-sm text-gray-500">
                        When enabled, step titles will not be displayed on the form.  
                      </p>
                    </div>
                  </div>

                  {/* Organization Branding Section */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Branding</h3>
                    <div className="space-y-4">
                      <div className="flex items-start space-x-3">
                        <div className="flex items-center h-5">
                          <input
                            id="show-org-logo"
                            type="checkbox"
                            checked={form.settings?.show_org_logo || false}
                            onChange={e => updateSettings('show_org_logo', e.target.checked)}
                            className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-gray-300 rounded"
                          />
                        </div>
                        <div className="flex-1">
                          <label htmlFor="show-org-logo" className="block text-sm font-medium text-gray-700">
                            Show organization logo on form
                          </label>
                          <p className="mt-1 text-sm text-gray-500">
                            {organization?.logo_url 
                              ? `Display your organization's logo at the top of the form.`
                              : `Upload an organization logo in Settings to enable this option.`
                            }
                          </p>
                          {organization?.logo_url && form.settings?.show_org_logo && (
                            <div className="mt-3 flex items-center space-x-3">
                              <img 
                                src={organization.logo_url} 
                                alt={`${organization.name} logo`}
                                className="h-8 w-auto object-contain"
                              />
                              <span className="text-sm text-gray-600">Preview of how it will appear</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Button Text Customization Section */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Button Text</h3>
                    <p className="text-sm text-gray-600 mb-6">
                      Customize the text displayed on form navigation buttons.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Next Button Text
                        </label>
                        <input
                          type="text"
                          value={form.settings?.button_text_next || ''}
                          onChange={e => updateSettings('button_text_next', e.target.value)}
                          placeholder="Continue →"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                        />
                        <p className="mt-1 text-sm text-gray-500">
                          Text for advancing to the next step
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Submit Button Text
                        </label>
                        <input
                          type="text"
                          value={form.settings?.button_text_submit || ''}
                          onChange={e => updateSettings('button_text_submit', e.target.value)}
                          placeholder="✓ Submit"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                        />
                        <p className="mt-1 text-sm text-gray-500">
                          Text for the final submission button
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Back Button Text
                        </label>
                        <input
                          type="text"
                          value={form.settings?.button_text_back || ''}
                          onChange={e => updateSettings('button_text_back', e.target.value)}
                          placeholder="← Back"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                        />
                        <p className="mt-1 text-sm text-gray-500">
                          Text for going to the previous step
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Meta Pixel Tracking Section */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Meta Pixel Tracking</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                      Add Meta (Facebook) Pixel tracking to your form to track conversions and optimize your ads.
                    </p>
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                          Meta Pixel ID
                        </label>
                        <input
                          type="text"
                          value={form.settings?.meta_pixel_id || ''}
                          onChange={e => updateSettings('meta_pixel_id', e.target.value)}
                          placeholder="1142342144518138"
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent dark:bg-gray-800 dark:text-gray-100"
                        />
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                          Enter your Meta Pixel ID to track page views and form interactions
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                          Custom Event Name (Optional)
                        </label>
                        <input
                          type="text"
                          value={form.settings?.meta_pixel_event || ''}
                          onChange={e => updateSettings('meta_pixel_event', e.target.value)}
                          placeholder="FormSubmitted"
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent dark:bg-gray-800 dark:text-gray-100"
                        />
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                          Custom event name to fire when the form is submitted (e.g., "Lead", "Purchase", "Subscribe")
                        </p>
                      </div>

                      {form.settings?.meta_pixel_id && (
                        <div className="bg-brand-50 dark:bg-brand-900/20 border border-brand-200 dark:border-brand-800 rounded-lg p-4">
                          <div className="flex">
                            <div className="flex-shrink-0">
                              <svg className="h-5 w-5 text-brand-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                              </svg>
                            </div>
                            <div className="ml-3">
                              <h3 className="text-sm font-medium text-brand-800 dark:text-brand-200">
                                Meta Pixel Configuration Active
                              </h3>
                              <div className="mt-2 text-sm text-brand-700 dark:text-brand-300">
                                <p>The Meta Pixel will automatically:</p>
                                <ul className="list-disc list-inside mt-1 space-y-1">
                                  <li>Load when users visit your form</li>
                                  <li>Track a PageView event</li>
                                  {form.settings?.meta_pixel_event && (
                                    <li>Fire a "{form.settings.meta_pixel_event}" event when the form is submitted</li>
                                  )}
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'integrations' && (
    <div className="flex-1 p-6 overflow-y-auto">
            <div className="max-w-3xl mx-auto">
              <div className="mb-6">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Integrations</h2>
                <p className="text-gray-600 dark:text-gray-400">
                  Connect your form to external services and automate your workflow.
                </p>
              </div>
              <IntegrationManager formId={id} />
            </div>
          </div>
        )}

        {activeTab === 'versions' && (
          <div className="flex-1 bg-gray-50 dark:bg-gray-900 overflow-hidden">
            <div className="h-full flex">
              {/* Timeline Section */}
              <div className={`transition-all duration-300 ease-in-out bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 ${
                selectedVersionId ? 'w-1/2' : 'w-full'
              }`}>
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Version History</h2>
                  <p className="text-gray-600 dark:text-gray-400">
                    View and compare different versions of your form.
                  </p>
                </div>
                
                <div className="p-6 overflow-y-auto" style={{ height: 'calc(100vh - var(--app-chrome-height) - 96px)' }}>
                  {versions.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No versions yet</h3>
                      <p className="text-gray-500 dark:text-gray-400">Make changes to your form to create versions</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Timeline */}
                      <div className="relative">
                        {/* Timeline line */}
                        <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700 z-10"></div>
                        
                        {versions.map((version, index) => (
                          <div 
                            key={version.id} 
                            className={`relative flex items-start space-x-4 pb-8 cursor-pointer group transition-all duration-200 ${
                              selectedVersionId === version.id ? 'scale-[0.98] opacity-90' : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                            }`}
                            onClick={() => setSelectedVersionId(selectedVersionId === version.id ? null : version.id)}
                          >
                            {/* Timeline dot */}
                            <div className={`relative z-10 flex-shrink-0 w-4 h-4 rounded-full border-2 transition-colors ${
                              !version.effective_to 
                                ? 'bg-brand-500 border-brand-500' 
                                : selectedVersionId === version.id
                                ? 'bg-brand-500 border-brand-500'
                                : 'bg-white dark:bg-gray-900 border-gray-300 dark:border-gray-600'
                            }`}>
                              {!version.effective_to && (
                                <div className="absolute -inset-1 bg-brand-500 rounded-full animate-ping opacity-20"></div>
                              )}
                            </div>
                            
                            {/* Version card */}
                            <div className={`flex-1 min-w-0 bg-white dark:bg-gray-800 rounded-lg border transition-all duration-200 ${
                              selectedVersionId === version.id 
                                ? 'border-brand-200 dark:border-brand-700 shadow-md' 
                                : 'border-gray-200 dark:border-gray-700 group-hover:border-gray-300 dark:group-hover:border-gray-600'
                            }`}>
                              <div className="p-4">
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center space-x-3">
                                    <span className="font-semibold text-lg text-gray-900 dark:text-gray-100">
                                      v{version.version_number}
                                    </span>
                                    {!version.effective_to && (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-brand-100 text-brand-800 dark:bg-brand-900 dark:text-brand-200">
                                        Current
                                      </span>
                                    )}
                                  </div>
                                  <span className="text-sm text-gray-500 dark:text-gray-400">
                                    {new Date(version.created_at).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric'
                                    })}
                                  </span>
                                </div>
                                
                                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-1">
                                  {version.title}
                                </h4>
                                
                                {version.change_summary && (
                                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                                    {version.change_summary}
                                  </p>
                                )}
                                
                                {/* Enhanced version details */}
                                <div className="space-y-2 mb-3">
                                  {version.fields?.steps && version.fields.steps.length > 0 ? (
                                    <div className="text-xs text-gray-600 dark:text-gray-400">
                                      <div className="flex items-center space-x-4 mb-1">
                                        <span>
                                          {version.fields.steps.length} step{version.fields.steps.length !== 1 ? 's' : ''}
                                        </span>
                                        <span>
                                          {version.fields.steps.reduce((total, step) => total + (step.fields?.length || 0), 0)} fields
                                        </span>
                                      </div>
                                      <div className="text-xs text-gray-500 dark:text-gray-500">
                                        Steps: {version.fields.steps.map(step => step.title || `Step ${step.order || 1}`).join(', ')}
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="text-xs text-gray-600 dark:text-gray-400">
                                      0 fields
                                    </div>
                                  )}
                                </div>
                                
                                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                                  <span className="text-gray-400">
                                    {new Date(version.created_at).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })}
                                  </span>
                                  {selectedVersionId === version.id ? (
                                    <span className="text-brand-600 dark:text-brand-400 font-medium">Click to close diff</span>
                                  ) : (
                                    <span className="text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300">Click to view diff</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Diff Section */}
              {selectedVersionId && (
                <div className="w-1/2 bg-white dark:bg-gray-900 animate-in slide-in-from-right duration-300">
                  <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Version Comparison</h3>
                      <button
                        onClick={() => setSelectedVersionId(null)}
                        className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  
                  <div className="p-6 overflow-y-auto" style={{ height: 'calc(100vh - var(--app-chrome-height) - 136px)' }}>
                    <VersionDiff 
                      selectedVersion={versions.find(v => v.id === selectedVersionId)}
                      currentVersion={versions.find(v => !v.effective_to)}
                      versions={versions}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

      {/* Embed Modal */}
      {showEmbedModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Embed Form</h2>
              <button
                onClick={() => setShowEmbedModal(false)}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <span className="sr-only">Close</span>
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Add your form to any website with these simple embed options. Choose the method that works best for your needs.
              </div>

              {/* Method 1: Simple Embed */}
              <div className="space-y-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Method 1: Simple Embed (Recommended)</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Copy and paste this code where you want your form to appear on your website:
                </p>
                <div className="relative">
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
{`<div data-fastestform-embed="${id}"></div>
<script src="${window.location.origin}/embed/embed.js"></script>`}
                  </pre>
                  <button
                    onClick={() => copyEmbedSnippet(`<div data-fastestform-embed="${id}"></div>\n<script src="${window.location.origin}/embed/embed.js"></script>`, 'simple')}
                    className="absolute top-2 right-2 bg-gray-700 hover:bg-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700 text-white px-3 py-1 rounded text-xs"
                  >
                    {copiedSnippet === 'simple' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>

              {/* Method 2: Custom Height */}
              <div className="space-y-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Method 2: Custom Height</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Set a specific height for your form:
                </p>
                <div className="relative">
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
{`<div data-fastestform-embed="${id}" data-height="800px"></div>
<script src="${window.location.origin}/embed/embed.js"></script>`}
                  </pre>
                  <button
                    onClick={() => copyEmbedSnippet(`<div data-fastestform-embed="${id}" data-height="800px"></div>\n<script src="${window.location.origin}/embed/embed.js"></script>`, 'height')}
                    className="absolute top-2 right-2 bg-gray-700 hover:bg-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700 text-white px-3 py-1 rounded text-xs"
                  >
                    {copiedSnippet === 'height' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>

              {/* Method 3: Popup */}
              <div className="space-y-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Method 3: Popup/Modal</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Add a button that opens your form in a popup window:
                </p>
                <div className="relative">
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-sm overflow-x-auto">
{`<button data-fastestform-popup="${id}">Open Form</button>
<script src="${window.location.origin}/embed/embed.js"></script>`}
                  </pre>
                  <button
                    onClick={() => copyEmbedSnippet(`<button data-fastestform-popup="${id}">Open Form</button>\n<script src="${window.location.origin}/embed/embed.js"></script>`, 'popup')}
                    className="absolute top-2 right-2 bg-gray-700 hover:bg-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700 text-white px-3 py-1 rounded text-xs"
                  >
                    {copiedSnippet === 'popup' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>

              {/* Customization Options */}
              <div className="space-y-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Customization Options</h3>
                <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <strong>data-height</strong>
                      <p className="text-gray-600 dark:text-gray-400">Set form height (e.g., "600px", "80vh")</p>
                    </div>
                    <div>
                      <strong>data-width</strong>
                      <p className="text-gray-600 dark:text-gray-400">Set form width (e.g., "100%", "800px")</p>
                    </div>
                    <div>
                      <strong>data-max-width</strong>
                      <p className="text-gray-600 dark:text-gray-400">Max width for popups (e.g., "900px")</p>
                    </div>
                    <div>
                      <strong>data-auto-resize</strong>
                      <p className="text-gray-600 dark:text-gray-400">Auto-resize height (default: true)</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Instructions */}
              <div className="bg-brand-50 dark:bg-brand-900/20 border border-brand-200 dark:border-brand-800 rounded-lg p-4">
                <h4 className="font-medium text-brand-900 dark:text-brand-200 mb-2">How to use:</h4>
                <ol className="list-decimal list-inside text-sm text-brand-800 dark:text-brand-200 space-y-1">
                  <li>Copy one of the code snippets above</li>
                  <li>Paste it into your website's HTML where you want the form to appear</li>
                  <li>Save and publish your website</li>
                  <li>Your form will automatically load and be ready to collect responses!</li>
                </ol>
              </div>

              <div className="text-center">
                <a
                  href={`${window.location.origin}/embed/demo`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-600 dark:text-brand-400 hover:text-brand-800 dark:hover:text-brand-300 text-sm underline"
                >
                  View live examples and documentation →
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Save as Template Modal */}
      {showSaveTemplateModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) setShowSaveTemplateModal(false); }}
        >
          <div className="w-full max-w-md mx-4 bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Save as Template</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Save this form as a reusable template for your team.
            </p>
            <form onSubmit={handleSaveAsTemplate} className="mt-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Template Name
                </label>
                <input
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="e.g. Customer Feedback Form"
                  className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-shadow"
                  required
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Category
                </label>
                <input
                  type="text"
                  value={templateCategory}
                  onChange={(e) => setTemplateCategory(e.target.value)}
                  placeholder="e.g. Feedback, Lead Generation, HR"
                  className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-shadow"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Description <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e.target.value)}
                  placeholder="Briefly describe what this template is for..."
                  className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-shadow"
                  rows="3"
                />
              </div>
              <div className="text-xs text-gray-400 dark:text-gray-500">
                Template will include {steps.reduce((c, s) => c + s.fields.length, 0)} field{steps.reduce((c, s) => c + s.fields.length, 0) !== 1 ? 's' : ''} across {steps.length} step{steps.length !== 1 ? 's' : ''} and all current settings.
              </div>
              <div className="flex justify-end gap-2.5 pt-3 border-t border-gray-100 dark:border-gray-800">
                <button
                  type="button"
                  onClick={() => setShowSaveTemplateModal(false)}
                  className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingTemplate}
                  className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50"
                >
                  {savingTemplate ? 'Saving...' : 'Save Template'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default FormBuilder;
